import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Issue } from "@adsk/acc-issues-types";
import { Sheet } from "@adsk/acc-issues-core/state/redux/ducks/issuesCollection";
import type { IssuePlacementLinkParams } from "@adsk/acc-issues-core/providers/IssuePlacementContext";
import { useDispatch } from "@adsk/acc-issues-core/state/redux/hooks";
import {
  fetchIssuesSheetAssociation,
  SHEET_SURFACE_TYPE,
} from "@adsk/acc-issues-core/state/redux/ducks/sheets";
import { fetchIssuesDocumentAssociations } from "@adsk/acc-issues-core/state/redux/ducks/documents";
import { hasFilePlacement } from "@adsk/acc-issues-core/utils/data/placement";
import { useCurrentProductId } from "@adsk/alloy-provider";
import { useIssuePlacement } from "@adsk/acc-issues-core/providers/IssuePlacementContext";
import usePlacementData from "../../../IssueDetailPanel/IssueDetailTabPanel/hooks/PlacementFieldHooks/usePlacementData";
import { useReferencePickerPlugins } from "../../../input-field-components/ReferencePickerField/useReferencePickerPlugins";
import { DeletedIssuesSpaceContext } from "@adsk/acc-issues-core/providers/DeletedIssuesSpaceContext";

type IssueRow = Issue & {
  sheet?: Sheet | undefined;
};
export const useIssuesTableData = (
  issues: IssueRow[],
  options: { shouldFetchAssociations?: boolean } = {
    // associations may already be fetched with fetchIssuesAction,
    // so this only needs to be true when issue data wasn't fetched that way
    shouldFetchAssociations: true,
  }
) => {
  const dispatch = useDispatch();
  const allIssueUids = issues.map(issue => issue.id);
  const allIssueUidsKey = JSON.stringify(allIssueUids);
  const [fetchedIssueIds, setFetchedIssueIds] = useState([] as string[]);
  const getPlacementLink = useIssuePlacement();
  const referencePickerPlugins = useReferencePickerPlugins();

  const getPlacementLinkRef = useRef(getPlacementLink);
  useEffect(() => {
    getPlacementLinkRef.current = getPlacementLink;
  }, [getPlacementLink]);

  const stableReferences = useMemo(
    () => ({ plugins: referencePickerPlugins }),
    [referencePickerPlugins]
  );

  const isDeletedSpace = useContext(DeletedIssuesSpaceContext);

  useEffect(() => {
    if (!options.shouldFetchAssociations) {
      return;
    }
    // we want to fetch sheet association for only the new uids...
    // this is important in part because if too many uids are provided
    // to the sheet association search endpoint, it forces it to return paginated results
    // in a way that our code is not set up to handle... we must always only be searching for
    // fewer than 50 sheet associations at a time.
    const allIssuesToFetch = issues.filter(
      issue => !fetchedIssueIds.includes(issue.id)
    );
    const issueUidsToFetch = allIssuesToFetch.map(issue => issue.id);
    setFetchedIssueIds(fetchedIssueIds.concat(issueUidsToFetch));
    dispatch(
      fetchIssuesSheetAssociation({
        issues: allIssuesToFetch,
        useCache: true,
        includeDeletedMarkups: isDeletedSpace,
      })
    );
    dispatch(
      fetchIssuesDocumentAssociations({
        issues,
        useCache: true,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allIssueUidsKey]);
  const productId = useCurrentProductId();
  const getPlacementData = usePlacementData();

  return useMemo(
    () =>
      issues.map(issue => {
        const placementData = getPlacementData(issue);
        const sheetAssociation = placementData.sheetAssociation;
        const isSheetPlacementAssociation =
          sheetAssociation?.surfaceType === SHEET_SURFACE_TYPE;

        const placementEntity = isSheetPlacementAssociation
          ? placementData.sheet
          : placementData.document;
        const hasPlacementDisplayError = placementData.hasPlacementError;
        const isPlacementDeleted = placementData.isPlacementDeleted;

        // Table uses tri-state placement for rendering:
        // - undefined => loading spinner
        // - null => loaded (empty/deleted/error)
        // - entity => show placement text/link
        const placement = placementData.isPlacementLoading
          ? undefined
          : hasPlacementDisplayError || isPlacementDeleted
          ? null
          : placementEntity ?? null;

        const hasDocumentPlacement = !!(
          hasFilePlacement(issue) ||
          (!isSheetPlacementAssociation && placementData.document) ||
          (!isSheetPlacementAssociation && placementData.isPlacementDeleted)
        );

        return {
          ...issue,
          placement,
          references: stableReferences,
          isDocumentPlacement: hasDocumentPlacement,
          isSheetPlacement: isSheetPlacementAssociation,
          hasPlacementDisplayError,
          isPlacementDeleted,
          productId,
          placementLinkFunction: (params: IssuePlacementLinkParams) =>
            getPlacementLinkRef.current(params),
        };
      }),
    [issues, stableReferences, productId, getPlacementData]
  );
};
